import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ImageOverlay,
  MapContainer,
  Marker,
  Polyline,
  Rectangle
} from 'react-leaflet'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import { useSyncFloorMap } from 'domain/krpano'
import { CRS, LatLngTuple, Map } from 'leaflet'
import { useRecoilValue } from 'recoil'
import {
  buildingModeState,
  listLinkState,
  listPointState,
  selectedAreaState,
  SelectedMode,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import Spinner from 'components/atoms/Spinner'

import useMap from './hooks/useMap'
import useMapItem from './hooks/useMapItem'
import useRadar from './hooks/useRadar'
import { numberIcon, rotatedMarkerIcon } from './CustomMarker'

type LeafletRef = {
  createPolylineRef: any
  polylineRef: any
  initPositionRef: any
  mapContainerRef: Map | any
  povMarkerRef: any
  radarDragFlag: {
    isRadarMode: boolean
    isDragging: boolean
    rotate: any | null
    hgap: any | null
    mouseHoldTime: any | null
  }
}

type Props = {
  imagePath: string
  isLoadingMap: boolean
  isUnregisteredMap: boolean
}

export default function LeafletMap({
  imagePath,
  isLoadingMap,
  isUnregisteredMap
}: Props) {
  const { t } = useTranslation()
  const [imgBounds, setImgBounds] = useState<LatLngTuple>([0, 0])
  const [imageLoading, setImageLoading] = useState<boolean>()

  const isAddingPoint = useRef<boolean>(false)
  // react leaflet components ref
  const leafletRef = useRef<LeafletRef>({
    createPolylineRef: null,
    polylineRef: [],
    initPositionRef: null,
    mapContainerRef: null,
    povMarkerRef: null,
    radarDragFlag: {
      isRadarMode: false,
      isDragging: false,
      rotate: null,
      hgap: null,
      mouseHoldTime: null
    }
  })
  const buildingMode = useRecoilValue(buildingModeState)
  const selectedArea = useRecoilValue(selectedAreaState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const points = useRecoilValue(listPointState)
  const links = useRecoilValue(listLinkState)

  const [radarReady, setRadarReady] = useState(() =>
    Boolean(currentSelectedPoint && pointDetail?.id === currentSelectedPoint)
  )

  const {
    linkEventHandler,
    markerEventHandler,
    handleUpdatePointImageByDrag,
    isSyncChangedPoint,
    isFetchingPoint
  } = useMapItem({
    leafletRef
  })

  const { MapEventHandler, handleAddPoint } = useMap({
    imgBounds,
    leafletRef
  })

  const { radarEventHandler, sync360ViewToArc } = useRadar({
    leafletRef,
    setRadarReady
  })

  sync360ViewToArc(rotatedMarkerIcon)

  // Store last point to reset radar immediately during render, preventing 1-frame flashes
  const prevSelectedPointRef = useRef(currentSelectedPoint)
  if (prevSelectedPointRef.current !== currentSelectedPoint) {
    setRadarReady(false)
    prevSelectedPointRef.current = currentSelectedPoint
  }

  // Effect 2: Show radar only after pointDetail has been fetched AND the 1-second
  // Krpano BLEND animation has finished physically transitioning the scene.
  useEffect(() => {
    if (currentSelectedPoint && pointDetail?.id === currentSelectedPoint) {
      if (!radarReady) {
        const timeout = setTimeout(() => {
          setRadarReady(true)
        }, 5000)
        return () => clearTimeout(timeout)
      }
    }
  }, [currentSelectedPoint, pointDetail?.id, radarReady])

  const displayRadar = useMemo(() => {
    if (!radarReady) return null
    // condition for display radar at selected point
    const radarPoint =
      selectedArea &&
      currentSelectedPoint &&
      currentSelectedPoint === pointDetail?.id
        ? points.find((point) => point.id === pointDetail?.id)
        : undefined

    if (radarPoint && radarPoint.image360) {
      const hlookat = new KrpanoEngine('preview').instance?.get('view.hlookat')

      const rotate =
        hlookat !== radarPoint.initView.h
          ? Krpano.NormalizeHGap(hlookat)
          : radarPoint.initView.h

      const frontMain = Krpano.GetFrontYaw(radarPoint, radarPoint.image360?.id)
      const activeImageId = selectedSubImage || radarPoint.image360?.id
      const frontActive = Krpano.GetFrontYaw(radarPoint, activeImageId)
      const delta = frontActive - frontMain
      const effectiveHGap = Krpano.NormalizeHGap(
        (radarPoint.initView.radarHGap || 0) - delta
      )

      return (
        <Marker
          key={radarPoint.id}
          position={[radarPoint.y, radarPoint.x]}
          ref={(el) => {
            leafletRef.current.povMarkerRef = el
          }}
          zIndexOffset={440 + points.length}
          icon={rotatedMarkerIcon(
            typeof leafletRef.current.radarDragFlag.rotate === 'number'
              ? leafletRef.current.radarDragFlag.rotate
              : Krpano.NormalizeHGap(rotate + effectiveHGap)
          )}
          draggable={false}
          eventHandlers={radarEventHandler(buildingMode.selectedMode)}
        />
      )
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    radarReady,
    currentSelectedPoint,
    selectedSubImage,
    pointDetail,
    buildingMode.selectedMode,
    selectedArea,
    leafletRef.current.radarDragFlag
  ])

  const { floorId } = useParams()

  const floorPoints = useMemo(
    () =>
      points?.filter(
        (point) => !point.floorId || String(point.floorId) === String(floorId)
      ) || [],
    [points, floorId]
  )

  const floorPointIdSet = useMemo(
    () => new Set(floorPoints.map((p) => p.id)),
    [floorPoints]
  )

  const floorLinks = useMemo(
    () =>
      links?.filter(
        (link) =>
          floorPointIdSet.has(link.point1.id) &&
          floorPointIdSet.has(link.point2.id)
      ) || [],
    [links, floorPointIdSet]
  )

  const { handleSyncMapOnImageChange } = useSyncFloorMap({
    imagePath,
    isUnregisteredMap,
    mapContainerRef: {
      get current() {
        return leafletRef.current.mapContainerRef
      }
    },
    setImgBounds,
    setImageLoading,
    maxBoundsPadding: { x: 1500, y: 500 }
  })

  const mapTarget = document.getElementById('map-container')
  if (mapTarget && selectedArea) {
    mapTarget.ondragover = (e) => e.preventDefault()
    mapTarget.ondrop = (e) => {
      e.preventDefault()
      if (!leafletRef.current.mapContainerRef) return
      const receivedData = e.dataTransfer?.getData('text') || ''
      if (!receivedData.startsWith('imagedrag-')) return

      const imageId = +receivedData.split('-')[1]
      const { lng, lat } =
        leafletRef.current.mapContainerRef.mouseEventToLatLng(e)
      const cord = leafletRef.current.mapContainerRef.mouseEventToLayerPoint(e)

      if (buildingMode.selectedMode !== 'point') return

      // check if drop position have point, if latlng is between point latlng +- 17 for min zoom
      const existedPoint = floorPoints.find((item) => {
        const pointCordinate =
          leafletRef.current.mapContainerRef.latLngToLayerPoint([
            item.y,
            item.x
          ])
        return (
          pointCordinate.x - 17 <= cord.x &&
          pointCordinate.x + 17 >= cord.x &&
          pointCordinate.y - 17 <= cord.y &&
          pointCordinate.y + 17 >= cord.y
        )
      })
      if (existedPoint) {
        if (!existedPoint.image360) {
          handleUpdatePointImageByDrag(existedPoint.id, imageId)
        } else {
          notification.error({
            message: t('translation.pointAlreadyHaveImage')
          })
        }
      } else {
        handleAddPoint(lng, lat, imgBounds, imageId)
      }
    }
    mapTarget.ondragenter = (e) => e.preventDefault()
    mapTarget.ondragleave = (e) => e.preventDefault()
  }

  if (isLoadingMap) return null

  return (
    <>
      {(isAddingPoint.current || imageLoading || isFetchingPoint) && (
        <Spinner className="z-[999]" />
      )}

      <MapContainer
        ref={(el) => {
          leafletRef.current.mapContainerRef = el
        }}
        center={[-99999, -99999]}
        zoom={0}
        zoomSnap={0.1}
        zoomDelta={0.5}
        id="map-container"
        className="area-map-container select-none"
        crs={CRS.Simple}
        zoomControl={false}
        attributionControl={false}
        whenReady={() => {
          handleSyncMapOnImageChange()
        }}
      >
        {/* handle map event */}
        <MapEventHandler />

        {floorPoints.map((point) => (
          <Marker
            draggable={
              buildingMode.selectedMode === SelectedMode.POINT &&
              !isSyncChangedPoint
            }
            bubblingMouseEvents={false}
            eventHandlers={markerEventHandler(point, imgBounds)}
            zIndexOffset={
              point.id === currentSelectedPoint
                ? 450 + floorPoints.length
                : 400 + point.order
            }
            // leaflet position is latlng with y=lat x=lng
            position={[point.y, point.x]}
            icon={numberIcon(
              point,
              selectedArea ? point.areaId === selectedArea : false,
              selectedArea ? point.id === currentSelectedPoint : false
            )}
            key={`${point.id}-${point.order}`}
          />
        ))}

        {floorLinks.map((link, index) => (
          <Polyline
            key={link.id}
            ref={(el) => {
              leafletRef.current.polylineRef[index] = { ref: el, id: link.id }
            }}
            // leaflet position is latlng with y=lat x=lng
            positions={[
              [link.point1.y, link.point1.x],
              [link.point2.y, link.point2.x]
            ]}
            color="#000"
            className="delete-able-link"
            weight={5}
            bubblingMouseEvents={false}
            eventHandlers={linkEventHandler(link.id, index)}
          />
        ))}

        {displayRadar}

        {isUnregisteredMap && imgBounds[0] && imgBounds[1] && (
          <>
            {(() => {
              const gridCells: JSX.Element[] = []
              const height = imgBounds[0]
              const width = imgBounds[1]
              const gridSize = 50
              const borderColor = '#e5e5e5'

              for (let y = 0; y < height; y += gridSize) {
                for (let x = 0; x < width; x += gridSize) {
                  const cellHeight = Math.min(gridSize, height - y)
                  const cellWidth = Math.min(gridSize, width - x)

                  gridCells.push(
                    <Rectangle
                      key={`grid-cell-${x}-${y}`}
                      bounds={[
                        [y, x],
                        [y + cellHeight, x + cellWidth]
                      ]}
                      pathOptions={{
                        color: borderColor,
                        fillColor: '#ffffff',
                        fillOpacity: 1,
                        weight: 1,
                        interactive: false
                      }}
                    />
                  )
                }
              }
              return gridCells
            })()}
          </>
        )}

        {!isUnregisteredMap && imgBounds[0] > 0 && imgBounds[1] > 0 && (
          <ImageOverlay url={imagePath} bounds={[[0, 0], imgBounds]} />
        )}
      </MapContainer>
    </>
  )
}
